import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { flashDeals, type FlashDeal } from '@/mocks/promos';
import { useCart } from '@/hooks/useCart';
import { useScrollReveal, useStaggerReveal } from '@/hooks/useScrollReveal';

const calcTime = (endsAt: string) => {
  const diff = Math.max(0, new Date(endsAt).getTime() - Date.now());
  return {
    h: Math.floor(diff / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
  };
};

const pad = (n: number) => String(n).padStart(2, '0');

const FlashTimer = ({ endsAt, color }: { endsAt: string; color: string }) => {
  const [time, setTime] = useState(() => calcTime(endsAt));

  useEffect(() => {
    const t = setInterval(() => setTime(calcTime(endsAt)), 1000);
    return () => clearInterval(t);
  }, [endsAt]);

  const units = [
    { val: time.h, label: 'ч' },
    { val: time.m, label: 'мин' },
    { val: time.s, label: 'сек' },
  ];

  return (
    <div className="flex items-center gap-1">
      {units.map((u, i) => (
        <div key={u.label} className="flex items-center gap-1">
          <div
            className="flex flex-col items-center px-2.5 py-1.5 rounded-lg min-w-[40px]"
            style={{ background: `${color}15`, border: `1px solid ${color}35` }}
          >
            <span
              className="font-orbitron font-black text-lg leading-none tabular-nums"
              style={{ color, textShadow: `0 0 10px ${color}80` }}
            >
              {pad(u.val)}
            </span>
            <span className="font-mono text-[9px] mt-0.5" style={{ color: 'rgba(255,255,255,0.3)' }}>{u.label}</span>
          </div>
          {i < units.length - 1 && (
            <span className="font-orbitron font-bold text-lg mb-3" style={{ color, opacity: 0.4 }}>:</span>
          )}
        </div>
      ))}
    </div>
  );
};

const FlashCard = ({ deal }: { deal: FlashDeal }) => {
  const { addItem, hasItem } = useCart();
  const inCart = hasItem(deal.id);
  const navigate = useNavigate();
  const stockPercent = Math.round((deal.sold / deal.stock) * 100);

  return (
    <div
      className="relative rounded-2xl overflow-hidden flex flex-col group cursor-pointer transition-all duration-300"
      style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${deal.color}20` }}
      onClick={() => navigate(`/product/${deal.id}`)}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${deal.color}50`; (e.currentTarget as HTMLElement).style.transform = 'translateY(-3px)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = `${deal.color}20`; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
    >
      {/* Discount badge */}
      <div className="absolute top-3 left-3 z-10">
        <span
          className="px-3 py-1 rounded-full font-orbitron font-black text-sm"
          style={{ background: deal.color, color: '#050508', boxShadow: `0 0 12px ${deal.color}80` }}
        >
          -{deal.discount}%
        </span>
      </div>

      {/* Flash badge */}
      <div className="absolute top-3 right-3 z-10 flex items-center gap-1 px-2 py-1 rounded-full font-mono text-[10px] font-bold animate-pulse" style={{ background: 'rgba(255,107,0,0.2)', border: '1px solid rgba(255,107,0,0.4)', color: '#ff6b00' }}>
        <i className="ri-flashlight-line text-xs" />
        FLASH
      </div>

      {/* Image */}
      <div
        className="relative h-48 flex items-center justify-center overflow-hidden"
        style={{ background: `linear-gradient(135deg, #0a0a14, ${deal.color}08)` }}
      >
        <div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-24 h-12 rounded-full opacity-20 group-hover:opacity-40 transition-opacity"
          style={{ background: deal.color, filter: 'blur(16px)' }}
        />
        <img
          src={deal.image}
          alt={deal.name}
          className="w-32 h-32 object-contain relative z-10 transition-transform duration-500 group-hover:scale-110 group-hover:-translate-y-1"
          style={{ filter: `drop-shadow(0 8px 16px ${deal.color}60)` }}
        />
      </div>

      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-orbitron font-bold text-sm text-white leading-snug mb-4 line-clamp-2">
          {deal.name}
        </h3>

        {/* Timer */}
        <div className="mb-4">
          <p className="font-mono text-xs mb-2 flex items-center gap-1.5" style={{ color: 'rgba(255,255,255,0.35)' }}>
            <i className="ri-alarm-line" style={{ color: '#ff6b00' }} />
            Осталось времени:
          </p>
          <FlashTimer endsAt={deal.endsAt} color={deal.color} />
        </div>

        {/* Stock bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="font-mono text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>Разобрали</span>
            <span className="font-mono text-xs font-bold" style={{ color: deal.color }}>{deal.sold} из {deal.stock}</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${stockPercent}%`,
                background: `linear-gradient(90deg, ${deal.color}, ${deal.color}80)`,
                boxShadow: `0 0 8px ${deal.color}60`,
              }}
            />
          </div>
          {stockPercent >= 70 && (
            <p className="font-mono text-xs mt-1 animate-pulse" style={{ color: '#ff6b00' }}>
              Почти разобрали! Осталось {deal.stock - deal.sold} шт.
            </p>
          )}
        </div>

        {/* Price */}
        <div className="flex items-end justify-between mb-4">
          <div>
            <div
              className="font-orbitron font-black text-2xl"
              style={{ color: deal.color, textShadow: `0 0 10px ${deal.color}60` }}
            >
              {deal.price.toLocaleString()} ₽
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="font-mono text-sm text-white/25 line-through">{deal.oldPrice.toLocaleString()} ₽</span>
              <span className="font-mono text-xs font-bold" style={{ color: '#39ff14' }}>
                -{(deal.oldPrice - deal.price).toLocaleString()} ₽
              </span>
            </div>
          </div>
        </div>

        {/* CTA */}
        <button
          onClick={e => { e.stopPropagation(); addItem({ id: deal.id, name: deal.name, price: deal.price, oldPrice: deal.oldPrice, image: deal.image, color: deal.color }); }}
          className="whitespace-nowrap w-full flex items-center justify-center gap-2 py-3 rounded-xl font-orbitron font-bold text-xs cursor-pointer transition-all duration-300"
          style={
            inCart
              ? { background: deal.color, color: '#050508', boxShadow: `0 0 16px ${deal.color}80` }
              : { border: `1px solid ${deal.color}`, color: deal.color }
          }
        >
          <i className={inCart ? 'ri-check-line' : 'ri-shopping-bag-line'} />
          {inCart ? 'В КОРЗИНЕ' : 'КУПИТЬ СЕЙЧАС'}
        </button>
      </div>

      {/* Glow line */}
      <div
        className="absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ background: `linear-gradient(90deg, transparent, ${deal.color}, transparent)` }}
      />
    </div>
  );
};

const PromoFlashDeals = () => {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({ threshold: 0.1 });
  const { containerRef: cardsRef, visibleItems } = useStaggerReveal(flashDeals.length, { threshold: 0.05 });

  return (
    <section className="px-6 lg:px-12 pb-20">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(28px)',
            transition: 'opacity 0.6s cubic-bezier(0.22,1,0.36,1), transform 0.6s cubic-bezier(0.22,1,0.36,1)',
          }}
        >
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div
                className="flex items-center gap-2 px-3 py-1.5 rounded-full font-mono text-xs font-bold animate-pulse"
                style={{ background: 'rgba(255,107,0,0.15)', border: '1px solid rgba(255,107,0,0.35)', color: '#ff6b00' }}
              >
                <i className="ri-flashlight-line" />
                FLASH SALE
              </div>
              <span className="font-mono text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>Ограниченное количество</span>
            </div>
            <h2 className="font-orbitron font-black text-3xl md:text-4xl text-white">
              ГОРЯЩИЕ<br />
              <span style={{ color: '#ff6b00', textShadow: '0 0 20px rgba(255,107,0,0.5)' }}>ПРЕДЛОЖЕНИЯ</span>
            </h2>
          </div>
          <p className="font-mono text-xs max-w-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
            Скидки до 50% на ограниченное количество товаров. Успейте купить до окончания таймера!
          </p>
        </div>

        {/* Cards */}
        <div
          ref={cardsRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {flashDeals.map((deal, i) => (
            <div
              key={deal.id}
              style={{
                opacity: visibleItems[i] ? 1 : 0,
                transform: visibleItems[i] ? 'translateY(0) scale(1)' : 'translateY(32px) scale(0.97)',
                transition: 'opacity 0.6s cubic-bezier(0.22,1,0.36,1), transform 0.6s cubic-bezier(0.22,1,0.36,1)',
              }}
            >
              <FlashCard deal={deal} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PromoFlashDeals;
